import { useTilt } from "@/hooks/useTilt";
import { useIntervalWhenVisible } from "@/hooks/useIntervalWhenVisible";
import { useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

const PRINCIPLES = [
    { n: "01", titleKey: "man.p1.title", bodyKey: "man.p1.body" },
    { n: "02", titleKey: "man.p2.title", bodyKey: "man.p2.body" },
    { n: "03", titleKey: "man.p3.title", bodyKey: "man.p3.body" },
];

// Mini interactive components at bottom of each card
function CrowdMini() {
    const { t } = useI18n();
    const [idx, setIdx] = useState(0);
    const ref = useRef(null);
    useIntervalWhenVisible(ref, () => setIdx((v) => (v + 1) % 4), 1400);
    const states = [
        { l: 0.15, tone: "#25A26A", bg: "var(--itly-surface-green)", labelKey: "man.mini.crowd.silenzio" },
        { l: 0.50, tone: "#E39A20", bg: "var(--itly-surface-amber)", labelKey: "man.mini.crowd.moderato" },
        { l: 0.90, tone: "#E8714C", bg: "var(--itly-surface-peach)", labelKey: "man.mini.crowd.pienissimo" },
        { l: 0.35, tone: "#25A26A", bg: "var(--itly-surface-green)", labelKey: "man.mini.crowd.tranquillo" },
    ];
    const s = states[idx];
    return (
        <div
            ref={ref}
            style={{
                marginTop: 20,
                background: s.bg,
                borderRadius: 12,
                padding: "12px 14px",
                transition: "background 500ms ease",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: s.tone,
                    letterSpacing: "0.02em",
                }}
            >
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: s.tone,
                        }}
                    />
                    {s.labelKey ? t(s.labelKey) : ""}
                </span>
            </div>
            <div
                style={{
                    height: 6,
                    background: "rgba(15,61,62,0.08)",
                    borderRadius: 999,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${s.l * 100}%`,
                        height: "100%",
                        background: s.tone,
                        borderRadius: 999,
                        transition: "width 500ms cubic-bezier(0.2, 0.7, 0.2, 1)",
                    }}
                />
            </div>
        </div>
    );
}

function PointsMini() {
    const { t } = useI18n();
    const [gemma, setGemma] = useState(false);
    const ref = useRef(null);
    useIntervalWhenVisible(ref, () => setGemma((v) => !v), 1800);
    const value = gemma ? 20 : 10;
    return (
        <div
            ref={ref}
            style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "12px 16px",
                background: gemma ? "var(--itly-peach-050)" : "var(--itly-teal-050)",
                borderRadius: 12,
                transition: "background 500ms ease",
            }}
        >
            <span
                key={value}
                className="itly-mini-count"
                style={{
                    fontFamily: "Fraunces, serif",
                    fontWeight: 700,
                    fontSize: 30,
                    color: "var(--itly-fg-strong)",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                }}
            >
                +{value}
            </span>
            <span
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    color: "var(--itly-muted)",
                    flex: 1,
                }}
            >
                {gemma ? t("man.mini.points.gemma") : t("man.mini.points.trattoria")}
            </span>
            {gemma && (
                <span key="badge" className="itly-x2-badge itly-x2-badge-small">
                    ✦
                </span>
            )}
        </div>
    );
}

function VerificatoMini() {
    const { t } = useI18n();
    const [i, setI] = useState(0);
    const ref = useRef(null);
    useIntervalWhenVisible(ref, () => setI((v) => (v + 1) % 3), 1600);
    const items = [
        t("man.mini.verify.cciaa"),
        t("man.mini.verify.register"),
        t("man.mini.verify.ateco"),
    ];
    return (
        <div
            ref={ref}
            style={{
                marginTop: 20,
                padding: "12px 14px",
                background: "var(--itly-teal-050)",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                color: "var(--itly-fg-strong)",
                fontWeight: 600,
            }}
        >
            <span
                style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: "#25A26A",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
                aria-hidden="true"
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" />
                </svg>
            </span>
            <span key={i} className="itly-mini-count">
                {items[i]}
            </span>
        </div>
    );
}

const MINIS = [VerificatoMini, CrowdMini, PointsMini];

function TiltCard({ p, i }) {
    const { t } = useI18n();
    const tilt = useTilt(6);
    const Mini = MINIS[i] || null;
    return (
        <div
            ref={tilt.ref}
            onMouseMove={tilt.onMouseMove}
            onMouseLeave={tilt.onMouseLeave}
            className="itly-manifesto-card itly-tilt"
            data-testid={`manifesto-${p.n}`}
            data-reveal="up"
            style={{
                padding: "36px 30px 30px",
                borderTop: "1px solid var(--itly-teal-900)",
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 16,
                boxShadow: "var(--itly-card-shadow)",
                transition: "transform 260ms cubic-bezier(0.2, 0.7, 0.2, 1)",
            }}
        >
            <span
                style={{
                    fontFamily: "Fraunces, serif",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "var(--itly-orange-500)",
                    letterSpacing: "0.05em",
                }}
            >
                ✦ {p.n}
            </span>
            <h3
                className="itly-serif"
                style={{
                    margin: "16px 0 12px",
                    fontSize: 28,
                    lineHeight: 1.1,
                    color: "var(--itly-fg-strong)",
                    letterSpacing: "-0.015em",
                }}
            >
                {t(p.titleKey)}
            </h3>
            <p
                style={{
                    margin: 0,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 15,
                    lineHeight: 1.65,
                    color: "var(--itly-muted)",
                }}
            >
                {t(p.bodyKey)}
            </p>
            {Mini && <Mini />}
        </div>
    );
}

export default function Manifesto() {
    const { t } = useI18n();
    return (
        <section
            id="manifesto"
            data-testid="manifesto"
            style={{
                position: "relative",
                background: "var(--itly-page)",
                padding: "104px 32px",
                overflow: "hidden",
            }}
            data-reveal="up"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ maxWidth: 820, marginBottom: 56 }}>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12.5,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            margin: 0,
                        }}
                    >
                        {t("manifesto.badge")}
                    </p>
                    <h2
                        className="itly-serif"
                        style={{
                            marginTop: 14,
                            marginBottom: 0,
                            fontSize: "clamp(38px, 4.4vw, 64px)",
                            lineHeight: 1.05,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.022em",
                        }}
                    >
                        {t("manifesto.h2_a")}{" "}
                        <em
                            style={{
                                fontStyle: "italic",
                                color: "var(--itly-orange-500)",
                                fontWeight: 500,
                            }}
                        >
                            {t("manifesto.h2_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            marginTop: 22,
                            fontFamily: "Inter, sans-serif",
                            fontSize: 17,
                            lineHeight: 1.6,
                            color: "var(--itly-muted)",
                            maxWidth: 640,
                        }}
                    >
                        {t("manifesto.sub")}
                    </p>

                    {/* Perché iniziamo dalle Marche */}
                    <div
                        data-testid="manifesto-mvp"
                        data-reveal="up"
                        style={{
                            marginTop: 32,
                            padding: "22px 24px",
                            background: "var(--itly-surface)",
                            border: "1px solid var(--itly-border)",
                            borderLeft: "3px solid var(--itly-orange-500)",
                            borderRadius: 14,
                            maxWidth: 720,
                            boxShadow: "var(--itly-card-shadow)",
                        }}
                    >
                        <div
                            style={{
                                fontFamily: "Inter, sans-serif",
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: "var(--itly-orange-700)",
                                marginBottom: 8,
                            }}
                        >
                            {t("manifesto.mvpBadge")}
                        </div>
                        <h3
                            className="itly-serif"
                            style={{
                                margin: "0 0 10px",
                                fontSize: 22,
                                lineHeight: 1.2,
                                color: "var(--itly-fg-strong)",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            {t("manifesto.mvpTitle")}
                        </h3>
                        <p
                            style={{
                                margin: 0,
                                fontFamily: "Inter, sans-serif",
                                fontSize: 15.5,
                                lineHeight: 1.6,
                                color: "var(--itly-muted)",
                            }}
                        >
                            {t("manifesto.mvpBody")}
                        </p>
                    </div>
                </div>

                <div
                    className="itly-manifesto-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 24,
                    }}
                >
                    {PRINCIPLES.map((p, i) => (
                        <TiltCard key={p.n} p={p} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
